/**
 * 简历生成页面 - 分步向导式交互
 * 支持6步流程：基本信息 -> 教育背景 -> 工作经历 -> 项目经验 -> 技能特长 -> 选择模板
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { jsPDF } from 'jspdf';
import {
  User,
  GraduationCap,
  Briefcase,
  FolderGit2,
  Wrench,
  Palette,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Plus,
  Trash2,
  Eye,
  Download,
  Check,
  AlertCircle,
  Sparkles,
  Layout,
  Zap,
  X,
  Upload,
  Star,
} from 'lucide-react';
import { App } from 'antd';
import { toCanvas } from 'html-to-image';
import {
  resumeTemplates,
  getTemplateComponent,
  type ResumeTemplateKey,
} from '../components/resumeTemplates';
import AuthModal from '../components/AuthModal';
import { auth } from '../services/auth';
import { trackExportResume } from '../services/behaviorTracker';

// ==================== 类型定义 ====================

interface BasicInfo {
  fullName: string;
  phone: string;
  email: string;
  jobTitle: string;
  workYears: string;
  location: string;
  avatar?: string;
  advantages?: string;
}

interface Education {
  id: string;
  school: string;
  major: string;
  degree: string;
  startDate: string;
  endDate: string;
}

interface WorkExperience {
  id: string;
  company: string;
  position: string;
  startDate: string;
  endDate: string;
  description: string;
}

interface Project {
  id: string;
  name: string;
  role: string;
  techStack: string;
  achievements: string;
  startDate: string;
  endDate: string;
}

interface Skills {
  professional: string;
  languages: string;
  certificates: string;
}

interface ResumeData {
  basicInfo: BasicInfo;
  education: Education[];
  workExperience: WorkExperience[];
  projects: Project[];
  skills: Skills;
  template: string;
}

interface Template {
  id: string;
  name: string;
  description: string;
  preview: string;
  color: string;
}

// ==================== 常量定义 ====================

const STEPS = [
  { id: 1, title: '基本信息', icon: User },
  { id: 2, title: '个人优势', icon: Star },
  { id: 3, title: '教育背景', icon: GraduationCap },
  { id: 4, title: '工作经历', icon: Briefcase },
  { id: 5, title: '项目经验', icon: FolderGit2 },
  { id: 6, title: '技能特长', icon: Wrench },
  { id: 7, title: '选择模板', icon: Palette },
] as const;

const DEGREES = ['高中', '大专', '本科', '硕士', '博士', '其他'];

const WORK_YEARS_OPTIONS = [
  '应届生',
  '1年以下',
  '1-3年',
  '3-5年',
  '5-10年',
  '10年以上',
];

const TEMPLATES: Template[] = [
  {
    id: 'elegant',
    name: '优雅专业',
    description: '卡片式布局，适合追求专业的求职者',
    preview: 'bg-gradient-to-br from-[#1a5f5f]/30 to-[#2d8a8a]/20',
    color: 'from-[#1a5f5f] to-[#2d8a8a]',
  },
  {
    id: 'even',
    name: '极简现代',
    description: '扁平化设计，大量留白，适合简约风格',
    preview: 'bg-gradient-to-br from-gray-100/50 to-gray-300/30',
    color: 'from-gray-700 to-gray-900',
  },
  {
    id: 'kendall',
    name: '商务经典',
    description: '传统商务风格，适合金融/传统行业',
    preview: 'bg-gradient-to-br from-[#2c3e50]/30 to-[#34495e]/20',
    color: 'from-[#2c3e50] to-[#34495e]',
  },
  {
    id: 'caffeine',
    name: '创意大胆',
    description: '深色背景霓虹配色，适合创意行业',
    preview:
      'bg-gradient-to-br from-[#1a1a2e]/80 via-[#16213e]/60 to-[#0f3460]/40',
    color: 'from-purple-500 via-pink-500 to-cyan-500',
  },
  {
    id: 'gradientPro',
    name: '渐变专业',
    description: '琥珀金渐变背景，现代专业感',
    preview: 'bg-gradient-to-br from-amber-600 via-yellow-600 to-orange-600',
    color: 'from-amber-600 to-orange-600',
  },
  {
    id: 'magazine',
    name: '杂志排版',
    description: '不对称布局，杂志风格，衬线字体',
    preview: 'bg-gradient-to-br from-black to-gray-900',
    color: 'from-black to-gray-700',
  },
  {
    id: 'timeline',
    name: '时间线',
    description: '中央垂直时间线设计，左右分栏',
    preview: 'bg-gradient-to-br from-emerald-700 to-emerald-500',
    color: 'from-emerald-700 to-emerald-500',
  },
  {
    id: 'cards',
    name: '卡片堆叠',
    description: '独立卡片悬浮效果，彩色图标',
    preview: 'bg-gradient-to-br from-orange-400 to-pink-500',
    color: 'from-orange-400 to-pink-500',
  },
  {
    id: 'sidebarRight',
    name: '右侧边栏',
    description: '深色右侧边栏布局',
    preview: 'bg-gradient-to-br from-amber-900 to-orange-900',
    color: 'from-amber-600 to-orange-600',
  },
  {
    id: 'minimalLine',
    name: '极简线条',
    description: '细线条分隔，极简风格',
    preview: 'bg-gradient-to-br from-white to-gray-50',
    color: 'from-gray-800 to-black',
  },
  {
    id: 'colorBlock',
    name: '色块分区',
    description: '几何色块装饰，活泼风格',
    preview: 'bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500',
    color: 'from-blue-500 to-pink-500',
  },
  {
    id: 'typography',
    name: '字体排版',
    description: '超大号姓名，强调排版',
    preview: 'bg-gradient-to-br from-gray-900 to-black',
    color: 'from-gray-200 to-white',
  },
  {
    id: 'photoHeader',
    name: '照片头部',
    description: '全宽背景图头部',
    preview: 'bg-gradient-to-br from-amber-800 to-orange-900',
    color: 'from-amber-400 to-orange-400',
  },
  {
    id: 'twoColumn',
    name: '双栏紧凑',
    description: '高信息密度双栏布局',
    preview: 'bg-gradient-to-br from-slate-700 to-slate-900',
    color: 'from-slate-500 to-slate-700',
  },
  {
    id: 'modernBox',
    name: '现代方框',
    description: '直角设计，工业感',
    preview: 'bg-gradient-to-br from-gray-800 to-black',
    color: 'from-gray-400 to-gray-600',
  },
  {
    id: 'cleanWhite',
    name: '纯净白',
    description: '纯白背景，灰色文字，极简',
    preview: 'bg-gradient-to-br from-white to-gray-100',
    color: 'from-gray-600 to-gray-800',
  },
];

const INITIAL_RESUME_DATA: ResumeData = {
  basicInfo: {
    fullName: '',
    phone: '',
    email: '',
    jobTitle: '',
    workYears: '',
    location: '',
    advantages: '',
  },
  education: [],
  workExperience: [],
  projects: [],
  skills: {
    professional: '',
    languages: '',
    certificates: '',
  },
  template: 'elegant',
};

// ==================== 辅助函数 ====================

const generateId = () => Math.random().toString(36).substr(2, 9);

const validateEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const validatePhone = (phone: string): boolean => {
  return /^1[3-9]\d{9}$/.test(phone);
};

// ==================== 子组件 ====================

// 步骤指示器组件
interface StepIndicatorProps {
  currentStep: number;
  steps: typeof STEPS;
}

function StepIndicator({ currentStep, steps }: StepIndicatorProps) {
  return (
    <div className="w-full py-6 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-start justify-center">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            const isLast = index === steps.length - 1;

            return (
              <div key={step.id} className="flex items-start">
                {/* 步骤圆点 */}
                <div className="flex flex-col items-center">
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      transition-all duration-300 flex-shrink-0
                      ${
                        isActive
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white scale-110'
                          : isCompleted
                            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50'
                            : 'bg-slate-800/60 text-slate-400 border border-slate-600/50'
                      }
                    `}
                  >
                    {isCompleted ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span
                    className={`
                      mt-2 text-xs font-medium transition-colors duration-300 whitespace-nowrap
                      ${
                        isActive
                          ? 'text-white'
                          : isCompleted
                            ? 'text-amber-400'
                            : 'text-slate-500'
                      }
                    `}
                  >
                    {step.title}
                  </span>
                </div>

                {/* 连接线 */}
                {!isLast && (
                  <div className="w-16 md:w-24 lg:w-32 mx-2 h-0.5 mt-5">
                    <div
                      className={`
                        h-full rounded-full transition-all duration-500
                        ${
                          isCompleted
                            ? 'bg-gradient-to-r from-amber-500 to-amber-500/50'
                            : 'bg-slate-700'
                        }
                      `}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// 输入框组件
interface InputFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  error?: string;
  icon?: React.ReactNode;
}

function InputField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required = false,
  error,
  icon,
}: InputFieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1 text-sm font-medium text-slate-300">
        {label}
        {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            {icon}
          </div>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`
            w-full px-4 py-2.5 rounded-lg
            bg-slate-800/60 border
            ${error ? 'border-red-500/50' : 'border-slate-700'}
            text-white placeholder-slate-500
            focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30
            transition-all duration-200
            ${icon ? 'pl-10' : ''}
          `}
        />
      </div>
      {error && (
        <p className="text-xs text-red-400 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
}

// 选择框组件
interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  required?: boolean;
  error?: string;
}

function SelectField({
  label,
  value,
  onChange,
  options,
  required = false,
  error,
}: SelectFieldProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1 text-sm font-medium text-slate-300">
        {label}
        {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onClick={() => setIsOpen(!isOpen)}
          onBlur={() => setIsOpen(false)}
          className={`
            w-full px-4 py-2.5 rounded-lg
            bg-slate-800/60 border
            ${error ? 'border-red-500/50' : 'border-slate-700'}
            text-white appearance-none
            focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30
            transition-all duration-200
            cursor-pointer
          `}
        >
          <option value="" className="bg-slate-800">
            请选择
          </option>
          {options.map((option) => (
            <option key={option} value={option} className="bg-slate-800">
              {option}
            </option>
          ))}
        </select>
        <ChevronDown
          className={`
            absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none
            transition-transform duration-200
            ${isOpen ? 'rotate-180' : 'rotate-0'}
          `}
        />
      </div>
      {error && (
        <p className="text-xs text-red-400 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
}

// 文本域组件
interface TextAreaFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  required?: boolean;
}

function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
  required = false,
}: TextAreaFieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1 text-sm font-medium text-slate-300">
        {label}
        {required && <span className="text-red-400">*</span>}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="
          w-full px-4 py-3 rounded-lg
          bg-slate-800/60 border border-slate-700
          text-white placeholder-slate-500
          focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30
          transition-all duration-200 resize-none
        "
      />
    </div>
  );
}

// 日期范围组件
interface DateRangeFieldProps {
  startDate: string;
  endDate: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  label?: string;
}

function DateRangeField({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  label = '时间范围',
}: DateRangeFieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-slate-300">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="month"
          value={startDate}
          onChange={(e) => onStartChange(e.target.value)}
          className="
            flex-1 px-3 py-2 rounded-lg
            bg-slate-800/60 border border-slate-700
            text-white text-sm
            focus:outline-none focus:border-amber-500/50
            transition-all duration-200
            cursor-pointer
          "
        />
        <span className="text-slate-500">至</span>
        <input
          type="month"
          value={endDate}
          onChange={(e) => onEndChange(e.target.value)}
          className="
            flex-1 px-3 py-2 rounded-lg
            bg-slate-800/60 border border-slate-700
            text-white text-sm
            focus:outline-none focus:border-amber-500/50
            transition-all duration-200
            cursor-pointer
          "
        />
      </div>
    </div>
  );
}

// 可折叠卡片组件
interface CollapsibleCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onDelete?: () => void;
  defaultExpanded?: boolean;
}

function CollapsibleCard({
  title,
  subtitle,
  children,
  onDelete,
  defaultExpanded = true,
}: CollapsibleCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="bg-slate-800/40 rounded-xl border border-slate-700/50 overflow-hidden">
      <div
        className="
          flex items-center justify-between px-4 py-3
          bg-slate-800/60 cursor-pointer
          hover:bg-slate-800/80 transition-colors duration-200
        "
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-white truncate">
            {title || '未填写'}
          </h4>
          {subtitle && (
            <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ChevronRight
            className={`
              w-5 h-5 text-slate-400 transition-transform duration-300
              ${isExpanded ? 'rotate-90' : ''}
            `}
          />
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="
                p-1.5 rounded-lg text-slate-400
                hover:text-red-400 hover:bg-red-500/10
                transition-all duration-200 cursor-pointer
              "
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div
        className={`
          overflow-hidden transition-all duration-300
          ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}
        `}
      >
        <div className="p-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

// 根据模板ID获取对应的模板组件
const renderTemplateById = (templateId: string, data: ResumeData) => {
  const TemplateComponent = getTemplateComponent(
    templateId as ResumeTemplateKey,
  );
  return <TemplateComponent data={data} />;
};

// 简历预览弹窗组件
interface ResumePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: ResumeData;
  template: string;
}

function ResumePreviewModal({
  isOpen,
  onClose,
  data,
  template,
}: ResumePreviewModalProps) {
  const previewRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-5xl h-[90vh] bg-slate-900 rounded-2xl overflow-hidden flex flex-col">
        {/* 弹窗头部 */}
        <div className="flex items-center justify-between px-6 py-4 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-amber-400" />
            <h3 className="text-lg font-semibold text-white">简历预览</h3>
            <span className="px-3 py-1 text-xs font-medium bg-amber-500/20 text-amber-300 rounded-full">
              {TEMPLATES.find((t) => t.id === template)?.name || '默认模板'}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-amber-500/20 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 预览内容区 */}
        <div className="flex-1 overflow-auto p-8 bg-slate-100">
          <div className="max-w-[210mm] mx-auto shadow-2xl">
            {renderTemplateById(template, data)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== 主组件 ====================

export default function ResumeGeneratePage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [resumeData, setResumeData] = useState<ResumeData>(INITIAL_RESUME_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewTemplateId, setPreviewTemplateId] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>(
    'login',
  );
  const { message } = App.useApp();

  // 更新基本信息
  const updateBasicInfo = (field: keyof BasicInfo, value: string) => {
    setResumeData((prev) => ({
      ...prev,
      basicInfo: { ...prev.basicInfo, [field]: value },
    }));
    // 清除对应字段的错误
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // 教育背景操作
  const addEducation = () => {
    const newEducation: Education = {
      id: generateId(),
      school: '',
      major: '',
      degree: '',
      startDate: '',
      endDate: '',
    };
    setResumeData((prev) => ({
      ...prev,
      education: [...prev.education, newEducation],
    }));
  };

  const updateEducation = (
    id: string,
    field: keyof Education,
    value: string,
  ) => {
    setResumeData((prev) => ({
      ...prev,
      education: prev.education.map((edu) =>
        edu.id === id ? { ...edu, [field]: value } : edu,
      ),
    }));
  };

  const removeEducation = (id: string) => {
    setResumeData((prev) => ({
      ...prev,
      education: prev.education.filter((edu) => edu.id !== id),
    }));
  };

  // 工作经历操作
  const addWorkExperience = () => {
    const newWork: WorkExperience = {
      id: generateId(),
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      description: '',
    };
    setResumeData((prev) => ({
      ...prev,
      workExperience: [...prev.workExperience, newWork],
    }));
  };

  const updateWorkExperience = (
    id: string,
    field: keyof WorkExperience,
    value: string,
  ) => {
    setResumeData((prev) => ({
      ...prev,
      workExperience: prev.workExperience.map((work) =>
        work.id === id ? { ...work, [field]: value } : work,
      ),
    }));
  };

  const removeWorkExperience = (id: string) => {
    setResumeData((prev) => ({
      ...prev,
      workExperience: prev.workExperience.filter((work) => work.id !== id),
    }));
  };

  // 项目经验操作
  const addProject = () => {
    const newProject: Project = {
      id: generateId(),
      name: '',
      role: '',
      techStack: '',
      achievements: '',
      startDate: '',
      endDate: '',
    };
    setResumeData((prev) => ({
      ...prev,
      projects: [...prev.projects, newProject],
    }));
  };

  const updateProject = (id: string, field: keyof Project, value: string) => {
    setResumeData((prev) => ({
      ...prev,
      projects: prev.projects.map((proj) =>
        proj.id === id ? { ...proj, [field]: value } : proj,
      ),
    }));
  };

  const removeProject = (id: string) => {
    setResumeData((prev) => ({
      ...prev,
      projects: prev.projects.filter((proj) => proj.id !== id),
    }));
  };

  // 技能特长操作
  const updateSkills = (field: keyof Skills, value: string) => {
    setResumeData((prev) => ({
      ...prev,
      skills: { ...prev.skills, [field]: value },
    }));
  };

  // 验证当前步骤
  const validateCurrentStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    switch (currentStep) {
      case 1:
        // 个人信息可选，不做强制校验
        break;
      case 2:
        // 教育背景可选，不做强制校验
        break;
      case 3:
        // 工作经历可选，不做强制校验
        break;
      case 4:
        // 项目经验可选，不做强制校验
        break;
      case 5:
        // 技能特长可选，不做强制校验
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 步骤切换
  const goToStep = (step: number) => {
    if (step < currentStep || validateCurrentStep()) {
      setCurrentStep(step);
      setErrors({});
    }
  };

  const goToNext = () => {
    if (validateCurrentStep() && currentStep < 7) {
      setCurrentStep((prev) => prev + 1);
      setErrors({});
    }
  };

  const goToPrev = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      setErrors({});
    }
  };

  // 导出简历
  const handleExport = async () => {
    // 检查用户是否已登录
    if (!auth.isAuthenticated()) {
      setAuthModalMode('login');
      setIsAuthModalOpen(true);
      return;
    }

    setIsExporting(true);
    try {
      // 创建临时容器用于渲染简历
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'fixed';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '210mm';
      document.body.appendChild(tempContainer);

      // 渲染简历模板到临时容器
      const TemplateComponent = getTemplateComponent(
        resumeData.template as ResumeTemplateKey,
      );

      const root = createRoot(tempContainer);
      root.render(<TemplateComponent data={resumeData} />);

      // 等待渲染完成
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // 获取渲染后的实际内容元素（第一个子元素）
      const resumeElement = tempContainer.firstElementChild as HTMLElement;
      if (!resumeElement) {
        throw new Error('简历渲染失败');
      }

      // 使用 html-to-image 的 toCanvas 方法导出
      // 提升质量参数以获得更清晰的输出
      const canvas = await toCanvas(resumeElement, {
        quality: 1,
        pixelRatio: 4,
        backgroundColor: '#ffffff',
      });

      // 创建PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = 210;
      const pdfHeight = 297;
      const margin = 10;
      const pageContentHeight = pdfHeight - margin * 2;

      // 计算图片尺寸
      const imgWidth = pdfWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // 生成分页文件名
      const jobTitle = resumeData.basicInfo.jobTitle?.trim();
      const fileName = jobTitle
        ? `${resumeData.basicInfo.fullName || '简历'}-${jobTitle}.pdf`
        : `${resumeData.basicInfo.fullName || '简历'}.pdf`;

      // 分页处理 - 计算需要的页数
      const totalPages = Math.ceil(imgHeight / pageContentHeight);

      // 添加页面
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) {
          pdf.addPage();
        }

        // 计算每页在原图中的垂直偏移（像素坐标）
        const scaleFactor = canvas.width / imgWidth;
        const pageHeightInCanvas = pageContentHeight * scaleFactor;
        const srcY = i * pageHeightInCanvas;
        const srcHeight = Math.min(pageHeightInCanvas, canvas.height - srcY);

        // 创建临时canvas来裁剪当前页的内容
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.floor(srcHeight);
        const pageCtx = pageCanvas.getContext('2d');

        if (pageCtx) {
          // 绘制当前页的内容（从原canvas裁剪）
          pageCtx.drawImage(
            canvas,
            0,
            srcY,
            canvas.width,
            Math.floor(srcHeight),
            0,
            0,
            canvas.width,
            Math.floor(srcHeight),
          );

          // 使用 JPEG 格式，质量 0.96，获得更清晰的输出
          const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.96);
          const pageImgHeight =
            (Math.floor(srcHeight) * imgWidth) / canvas.width;

          pdf.addImage(
            pageImgData,
            'JPEG',
            margin,
            margin,
            imgWidth,
            pageImgHeight,
          );
        }
      }

      // 下载PDF
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // 清理
      root.unmount();
      document.body.removeChild(tempContainer);

      message.success('简历导出成功！');

      // 上报导出简历埋点
      trackExportResume(resumeData.template, {
        fullName: resumeData.basicInfo.fullName,
        jobTitle: resumeData.basicInfo.jobTitle,
      });
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请重试');
    } finally {
      setIsExporting(false);
    }
  };

  // ==================== 渲染各步骤表单 ====================

  // 处理头像上传
  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        updateBasicInfo('avatar', result);
        e.target.value = '';
      };
      reader.readAsDataURL(file);
    }
  };

  // 删除头像
  const handleAvatarRemove = () => {
    updateBasicInfo('avatar', '');
  };

  const renderBasicInfo = () => (
    <div className="space-y-5">
      {/* 头像上传区域 */}
      <div className="flex items-center gap-6">
        <div className="relative">
          {resumeData.basicInfo.avatar ? (
            <div className="relative">
              <img
                src={resumeData.basicInfo.avatar}
                alt="头像"
                className="w-24 h-24 rounded-full object-cover border-4 border-slate-700"
              />
              <button
                onClick={handleAvatarRemove}
                className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-slate-800 border border-slate-600 rounded-full flex items-center justify-center text-slate-400 hover:text-red-400 hover:border-red-500/50 hover:bg-red-500/10 transition-all duration-200 cursor-pointer shadow-md"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="w-24 h-24 rounded-full bg-slate-800 border-4 border-dashed border-slate-600 flex items-center justify-center">
              <User className="w-10 h-10 text-slate-500" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            头像（选填）
          </label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-medium hover:from-amber-400 hover:to-amber-500 transition-all duration-300 cursor-pointer">
              <Upload className="w-4 h-4" />
              选择头像
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            </label>
            <span className="text-xs text-slate-500">支持 JPG、PNG 格式</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <InputField
          label="姓名"
          value={resumeData.basicInfo.fullName}
          onChange={(value) => updateBasicInfo('fullName', value)}
          placeholder="请输入"
        />
        <InputField
          label="手机号"
          value={resumeData.basicInfo.phone}
          onChange={(value) => updateBasicInfo('phone', value)}
          placeholder="请输入"
        />
        <InputField
          label="邮箱"
          value={resumeData.basicInfo.email}
          onChange={(value) => updateBasicInfo('email', value)}
          placeholder="请输入"
        />
        <InputField
          label="求职意向"
          value={resumeData.basicInfo.jobTitle}
          onChange={(value) => updateBasicInfo('jobTitle', value)}
          placeholder="请输入"
        />
        <SelectField
          label="工作年限"
          value={resumeData.basicInfo.workYears}
          onChange={(value) => updateBasicInfo('workYears', value)}
          options={WORK_YEARS_OPTIONS}
        />
        <InputField
          label="所在城市"
          value={resumeData.basicInfo.location}
          onChange={(value) => updateBasicInfo('location', value)}
          placeholder="请输入"
        />
      </div>
    </div>
  );

  const renderAdvantages = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          个人优势
        </label>
        <textarea
          value={resumeData.basicInfo.advantages || ''}
          onChange={(e) => updateBasicInfo('advantages', e.target.value)}
          placeholder="请输入您的个人优势，如核心技能、专业特长、个人亮点等。建议分点描述，每点一行。&#10;&#10;示例：&#10;• 5年前端开发经验，精通React/Vue等主流框架&#10;• 具备全栈开发能力，熟悉Node.js和数据库设计&#10;• 优秀的团队协作能力，曾带领10人团队完成项目交付"
          className="w-full h-64 px-4 py-3 rounded-lg bg-[#0B0F19]/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 transition-all duration-200 resize-none"
        />
        <p className="mt-2 text-xs text-slate-500">
          提示：分点描述您的核心优势，让招聘方快速了解您的亮点
        </p>
      </div>
    </div>
  );

  const renderEducation = () => (
    <div className="space-y-4">
      {resumeData.education.map((edu, index) => (
        <CollapsibleCard
          key={edu.id}
          title={edu.school || `教育经历 ${index + 1}`}
          subtitle={edu.major}
          onDelete={() => removeEducation(edu.id)}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="学校名称"
              value={edu.school}
              onChange={(value) => updateEducation(edu.id, 'school', value)}
              placeholder="请输入"
            />
            <InputField
              label="专业"
              value={edu.major}
              onChange={(value) => updateEducation(edu.id, 'major', value)}
              placeholder="请输入"
            />
            <SelectField
              label="学历"
              value={edu.degree}
              onChange={(value) => updateEducation(edu.id, 'degree', value)}
              options={DEGREES}
            />
            <DateRangeField
              startDate={edu.startDate}
              endDate={edu.endDate}
              onStartChange={(value) =>
                updateEducation(edu.id, 'startDate', value)
              }
              onEndChange={(value) => updateEducation(edu.id, 'endDate', value)}
            />
          </div>
        </CollapsibleCard>
      ))}

      <div className="flex justify-center">
        <button
          onClick={addEducation}
          className="
            w-2/3 py-4 rounded-xl border-2 border-dashed border-slate-600
            text-slate-400 hover:text-amber-400 hover:border-amber-500/50
            hover:bg-amber-500/5
            transition-all duration-200 cursor-pointer
            flex items-center justify-center gap-2
          "
        >
          <Plus className="w-5 h-5" />
          添加教育背景
        </button>
      </div>
    </div>
  );

  const renderWorkExperience = () => (
    <div className="space-y-4">
      {resumeData.workExperience.map((work, index) => (
        <CollapsibleCard
          key={work.id}
          title={work.company || `工作经历 ${index + 1}`}
          subtitle={work.position}
          onDelete={() => removeWorkExperience(work.id)}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="公司名称"
                value={work.company}
                onChange={(value) =>
                  updateWorkExperience(work.id, 'company', value)
                }
                placeholder="请输入"
              />
              <InputField
                label="职位"
                value={work.position}
                onChange={(value) =>
                  updateWorkExperience(work.id, 'position', value)
                }
                placeholder="请输入"
              />
            </div>
            <DateRangeField
              startDate={work.startDate}
              endDate={work.endDate}
              onStartChange={(value) =>
                updateWorkExperience(work.id, 'startDate', value)
              }
              onEndChange={(value) =>
                updateWorkExperience(work.id, 'endDate', value)
              }
            />
            <TextAreaField
              label="工作内容"
              value={work.description}
              onChange={(value) =>
                updateWorkExperience(work.id, 'description', value)
              }
              placeholder="请输入"
              rows={4}
            />
          </div>
        </CollapsibleCard>
      ))}

      <div className="flex justify-center">
        <button
          onClick={addWorkExperience}
          className="
            w-2/3 py-4 rounded-xl border-2 border-dashed border-slate-600
            text-slate-400 hover:text-amber-400 hover:border-amber-500/50
            hover:bg-amber-500/5
            transition-all duration-200 cursor-pointer
            flex items-center justify-center gap-2
          "
        >
          <Plus className="w-5 h-5" />
          添加工作经历
        </button>
      </div>
    </div>
  );

  const renderProjects = () => (
    <div className="space-y-4">
      {resumeData.projects.map((proj, index) => (
        <CollapsibleCard
          key={proj.id}
          title={proj.name || `项目经验 ${index + 1}`}
          subtitle={proj.role}
          onDelete={() => removeProject(proj.id)}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="项目名称"
                value={proj.name}
                onChange={(value) => updateProject(proj.id, 'name', value)}
                placeholder="请输入"
              />
              <InputField
                label="担任角色"
                value={proj.role}
                onChange={(value) => updateProject(proj.id, 'role', value)}
                placeholder="请输入"
              />
            </div>
            <DateRangeField
              startDate={proj.startDate}
              endDate={proj.endDate}
              onStartChange={(value) =>
                updateProject(proj.id, 'startDate', value)
              }
              onEndChange={(value) => updateProject(proj.id, 'endDate', value)}
            />
            <InputField
              label="技术栈"
              value={proj.techStack}
              onChange={(value) => updateProject(proj.id, 'techStack', value)}
              placeholder="请输入"
            />
            <TextAreaField
              label="项目介绍和成果"
              value={proj.achievements}
              onChange={(value) =>
                updateProject(proj.id, 'achievements', value)
              }
              placeholder="请输入"
              rows={4}
            />
          </div>
        </CollapsibleCard>
      ))}

      <div className="flex justify-center">
        <button
          onClick={addProject}
          className="
            w-2/3 py-4 rounded-xl border-2 border-dashed border-slate-600
            text-slate-400 hover:text-amber-400 hover:border-amber-500/50
            hover:bg-amber-500/5
            transition-all duration-200 cursor-pointer
            flex items-center justify-center gap-2
          "
        >
          <Plus className="w-5 h-5" />
          添加项目经验
        </button>
      </div>
    </div>
  );

  const renderSkills = () => (
    <div className="space-y-5">
      <TextAreaField
        label="专业技能"
        value={resumeData.skills.professional}
        onChange={(value) => updateSkills('professional', value)}
        placeholder="请输入"
        rows={4}
      />
      <TextAreaField
        label="语言能力"
        value={resumeData.skills.languages}
        onChange={(value) => updateSkills('languages', value)}
        placeholder="请输入"
        rows={4}
      />
      <TextAreaField
        label="证书资质"
        value={resumeData.skills.certificates}
        onChange={(value) => updateSkills('certificates', value)}
        placeholder="请输入"
        rows={4}
      />
    </div>
  );

  const renderTemplate = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {TEMPLATES.map((template) => (
          <div
            key={template.id}
            onClick={() =>
              setResumeData((prev) => ({ ...prev, template: template.id }))
            }
            className={`
              relative p-4 rounded-xl border-2 cursor-pointer
              transition-all duration-300
              ${
                resumeData.template === template.id
                  ? 'border-amber-500 bg-amber-500/10'
                  : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
              }
            `}
          >
            {/* 选中标记 */}
            {resumeData.template === template.id && (
              <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}

            {/* 信息 */}
            <h4 className="font-medium text-white">{template.name}</h4>
            <p className="text-sm text-slate-400 mt-1">
              {template.description}
            </p>

            {/* 预览按钮 */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                // 设置当前预览的模板ID，并打开预览
                setPreviewTemplateId(template.id);
                setIsPreviewOpen(true);
              }}
              className="
                mt-3 w-full py-2 rounded-lg text-sm font-medium
                bg-slate-700/50 text-slate-300
                hover:bg-amber-500/20 hover:text-amber-300
                transition-all duration-200 cursor-pointer
                flex items-center justify-center gap-2
              "
            >
              <Eye className="w-4 h-4" />
              预览效果
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  // 渲染当前步骤内容
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInfo();
      case 2:
        return renderAdvantages();
      case 3:
        return renderEducation();
      case 4:
        return renderWorkExperience();
      case 5:
        return renderProjects();
      case 6:
        return renderSkills();
      case 7:
        return renderTemplate();
      default:
        return null;
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B] pt-20"
      style={{ minHeight: '73vh' }}
    >
      {/* 步骤指示器 - 放在公共导航栏下方 */}
      <div className="bg-slate-900/80 border-b border-slate-800">
        <StepIndicator currentStep={currentStep} steps={STEPS} />
      </div>

      {/* 主内容区 */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="bg-slate-800/40 rounded-2xl border border-slate-700/50 p-6">
          {/* 步骤标题 */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-emerald-500/20 border border-amber-500/20 flex items-center justify-center">
              {(() => {
                const Icon = STEPS[currentStep - 1].icon;
                return <Icon className="w-5 h-5 text-amber-400" />;
              })()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {STEPS[currentStep - 1].title}
              </h2>
              <p className="text-sm text-slate-400">
                步骤 {currentStep} / {STEPS.length}
              </p>
            </div>
          </div>

          {/* 表单内容 */}
          {renderStepContent()}

          {/* 底部操作按钮 */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-700/50">
            <button
              onClick={goToPrev}
              disabled={currentStep === 1}
              className="
                flex items-center gap-2 px-6 py-2.5 rounded-lg
                border border-slate-600 text-slate-300
                hover:bg-slate-800 hover:text-white
                transition-all duration-200 cursor-pointer
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              <ChevronLeft className="w-4 h-4" />
              上一步
            </button>

            <div className="flex items-center gap-3">
              {currentStep === 7 ? (
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="
                    flex items-center gap-2 px-8 py-2.5 rounded-lg
                    bg-gradient-to-r from-amber-500 to-amber-600
                    text-white font-medium
                    hover:from-amber-400 hover:to-amber-500
                    shadow-lg shadow-amber-500/30
                    transition-all duration-300 cursor-pointer
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  {isExporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      导出中...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      导出简历
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={goToNext}
                  className="
                    flex items-center gap-2 px-8 py-2.5 rounded-lg
                    bg-gradient-to-r from-amber-500 to-amber-600
                    text-white font-medium
                    hover:from-amber-400 hover:to-amber-500
                    transition-all duration-300 cursor-pointer
                  "
                >
                  下一步
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 简历预览弹窗 */}
      <ResumePreviewModal
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewTemplateId('');
        }}
        data={resumeData}
        template={previewTemplateId || resumeData.template}
      />

      {/* 登录/注册弹窗 */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultMode={authModalMode}
      />
    </div>
  );
}
